/**
 * The canvas's view: where the drawing sits and how big it is (R6).
 *
 * The surface used to be a scroll box with `scale()` on its contents. It is a
 * transform now — `translate(x, y) scale(zoom)` — because the design's
 * gestures (drag the background to pan, ⌘/Ctrl+scroll to zoom around the
 * cursor, pinch on a touch screen) all need the same two numbers and a scroll
 * box owns one of them. Everything here is arithmetic on that pair so the
 * component can stay about pointers and the tests can stay about numbers.
 *
 * Screen coordinates are pixels from the surface's top-left; world
 * coordinates are the node positions the graph stores.
 */
export interface View { x: number; y: number; zoom: number }
export interface Rect { x: number; y: number; w: number; h: number }
export interface Point { x: number; y: number }

/** 30–200%, the design's range. */
export const ZOOM_MIN = 0.3;
export const ZOOM_MAX = 2;
/** What a fresh canvas opens at before it has fitted. */
export const DEFAULT_VIEW: View = { x: 24, y: 16, zoom: 1 };
/** One notch of the − / + buttons. */
export const ZOOM_STEP = 0.1;

export const clampZoom = (z: number): number => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

/** A screen point, in world coordinates. */
export const toWorld = (v: View, sx: number, sy: number): Point =>
  ({ x: (sx - v.x) / v.zoom, y: (sy - v.y) / v.zoom });

/** A world point, on screen. */
export const toScreen = (v: View, wx: number, wy: number): Point =>
  ({ x: wx * v.zoom + v.x, y: wy * v.zoom + v.y });

export const panBy = (v: View, dx: number, dy: number): View => ({ ...v, x: v.x + dx, y: v.y + dy });

/**
 * Change the zoom so the world point under the screen point (sx, sy) stays
 * exactly there — the cursor, for a wheel; the midpoint, for a pinch.
 */
export function zoomAround(v: View, zoom: number, sx: number, sy: number): View {
  const z = clampZoom(zoom);
  const w = toWorld(v, sx, sy);
  return { zoom: z, x: sx - w.x * z, y: sy - w.y * z };
}

/** The − / + buttons zoom about the middle of the surface, one notch at a time. */
export function zoomStep(v: View, dir: 1 | -1, width: number, height: number): View {
  const next = Math.round((v.zoom + dir * ZOOM_STEP) * 100) / 100;
  return zoomAround(v, next, width / 2, height / 2);
}

/** The wheel: ⌘/Ctrl zooms around the cursor by a gentle ratio; plain scroll pans. */
export function wheelView(
  v: View, e: { deltaX: number; deltaY: number; ctrlKey: boolean; metaKey: boolean }, sx: number, sy: number
): View {
  if (e.ctrlKey || e.metaKey) {
    // A trackpad pinch arrives as ctrl+wheel with small deltas; a mouse wheel
    // as ±100. Scaling by the delta keeps both feeling continuous.
    const factor = Math.exp(-e.deltaY * 0.0022);
    return zoomAround(v, v.zoom * factor, sx, sy);
  }
  return panBy(v, -e.deltaX, -e.deltaY);
}

/**
 * Two fingers: the view that keeps the world point under the first midpoint
 * under the second one, scaled by how far the fingers moved apart.
 */
export function pinchView(v: View, a0: Point, b0: Point, a1: Point, b1: Point): View {
  const dist = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
  const mid = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const d0 = dist(a0, b0);
  const d1 = dist(a1, b1);
  const m0 = mid(a0, b0);
  const m1 = mid(a1, b1);
  const scaled = zoomAround(v, v.zoom * (d0 > 0 ? d1 / d0 : 1), m0.x, m0.y);
  return panBy(scaled, m1.x - m0.x, m1.y - m0.y);
}

/** The box around every node. */
export function boundsOf(rects: Rect[]): Rect | null {
  if (!rects.length) return null;
  const minX = Math.min(...rects.map((r) => r.x));
  const minY = Math.min(...rects.map((r) => r.y));
  const maxX = Math.max(...rects.map((r) => r.x + r.w));
  const maxY = Math.max(...rects.map((r) => r.y + r.h));
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/**
 * Frame the whole drawing: zoomed out until it fits, never enlarged past 100%
 * (a two-card workflow at 200% is not "fitted"), centred both ways. Rounded
 * to 5% so the readout says a number a person would choose.
 */
export function fitView(rects: Rect[], width: number, height: number, pad = 40): View {
  const b = boundsOf(rects);
  if (!b || width <= 0 || height <= 0) return DEFAULT_VIEW;
  const raw = Math.min((width - 2 * pad) / Math.max(1, b.w), (height - 2 * pad) / Math.max(1, b.h));
  const zoom = clampZoom(Math.min(1, Math.floor(raw * 20) / 20));
  return {
    zoom,
    x: (width - b.w * zoom) / 2 - b.x * zoom,
    y: (height - b.h * zoom) / 2 - b.y * zoom,
  };
}

/**
 * The smallest pan that brings a world rect fully on screen, or null when it
 * already is. Keyboard navigation and "Show me" use this instead of
 * `scrollIntoView`, which a transformed surface has no scroll box for.
 */
export function revealRect(v: View, rect: Rect, width: number, height: number, margin = 24): View | null {
  const tl = toScreen(v, rect.x, rect.y);
  const br = toScreen(v, rect.x + rect.w, rect.y + rect.h);
  let dx = 0;
  let dy = 0;
  if (tl.x < margin) dx = margin - tl.x;
  else if (br.x > width - margin) dx = Math.max(margin - tl.x, (width - margin) - br.x);
  if (tl.y < margin) dy = margin - tl.y;
  else if (br.y > height - margin) dy = Math.max(margin - tl.y, (height - margin) - br.y);
  if (!dx && !dy) return null;
  return panBy(v, dx, dy);
}

/** The dot grid follows the drawing: its cell scales and its origin pans. */
export function gridStyle(v: View, cell: number): { backgroundSize: string; backgroundPosition: string } {
  const size = cell * v.zoom;
  return {
    backgroundSize: `${size}px ${size}px`,
    backgroundPosition: `${v.x + size / 2}px ${v.y + size / 2}px`,
  };
}
